import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import clsx from 'clsx';

import formStyles from '../PurchaseOrderForm.module.scss';

import { AutoNumeric } from '../../../../common/auto-numeric/AutoNumeric';
import { CommonProps } from './pricing-list/PricingItem';
import { calculateValue } from '../../../../../utils/helper-functions';
import { PricingCalculator } from '../../../../../utils/PricingCalculator';
import { useUserPermissions } from '../../../../../context/user/PermissionsProvider';
import { PoFormLabel, pricingConfig } from '../po-form-config';
import { useAutoPricingState } from './context/AutoPricingProvider';
import { FormPricingKeys } from './pricing-list/FormPricing';
import { initialAutoPricingState } from '../../../../../constants/constants';

const { min } = pricingConfig[FormPricingKeys.BiWeeklyRent].validations;

export const biWeeklyRentTestId = 'BiWeeklyRentComponent';

export const BiWeeklyRent = (props: CommonProps) => {
  const { index, field, state } = props;
  const { hasAutoPricing } = useUserPermissions();
  const { autoPricings } = useAutoPricingState();
  const [value, setValue] = useState('');
  const { biWeeklyRate: apiValue, weeklyRate, weeklyTerm } =
    autoPricings[state] || initialAutoPricingState;
  const [isDefaultFromPO, setIsDefaultFromPO] = useState(
    field.biWeeklyRent !== ''
  );

  const weeklyTermWatched = useWatch({ name: `pricing[${index}].weeklyTerm` });
  const weeklyRentWatched = useWatch({ name: `pricing[${index}].weeklyRent` });

  const biWeeklyRent = () =>
    calculateValue(PricingCalculator.biWeeklyRent, weeklyRentWatched);

  const fieldName = `pricing[${index}].biWeeklyRent`;

  useEffect(() => {
    if (
      weeklyTerm === null &&
      weeklyRate === null &&
      apiValue === null &&
      !!weeklyTermWatched &&
      !!weeklyRentWatched &&
      !isDefaultFromPO
    ) {
      //* Value to be calculated from input change
      const rent =
        !!weeklyTermWatched && !!weeklyRentWatched && biWeeklyRent() >= '1'
          ? biWeeklyRent()
          : '';
      setValue(rent);
    } else {
      setValue('');
      setIsDefaultFromPO(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weeklyTermWatched, weeklyRentWatched]);

  const imperativeValueCheck =
    !!value ||
    !!apiValue ||
    weeklyRentWatched === '' ||
    weeklyTermWatched === '';
  const imperativeValue =
    value !== '' ? value : apiValue ? apiValue.toString() : '';

  return useMemo(
    () => (
      <div data-testid={biWeeklyRentTestId}>
        <label htmlFor={fieldName}>{PoFormLabel.BiWeeklyRate}:</label>
        <div className={clsx(formStyles.iconField, formStyles.noFillField)}>
          <span>$</span>

          <Controller
            as={AutoNumeric}
            name={fieldName}
            defaultValue={field.biWeeklyRent}
            {...(imperativeValueCheck && {
              imperativeValue: imperativeValue?.toString(),
              imperativeMode: true
            })}
            options={{
              minimumValue: 0,
              decimalPlaces: 2,
              ...(!hasAutoPricing && {
                maximumValue: Number.MAX_SAFE_INTEGER
              })
            }}
            placeholder="--"
            disabled
            rules={{ required: true, min }}
          />
        </div>
      </div>
    ),
    [
      fieldName,
      field.biWeeklyRent,
      imperativeValueCheck,
      imperativeValue,
      hasAutoPricing
    ]
  );
};
